import type { InstanceShifts, ShiftCalendarCell, ShiftCalendarHeader } from '@/api/shifts';
import { getCareerColorHex } from '@/utils/careers';
import type { BlockSlotCell } from '@/utils/schedule-blocks';

// Pure view-model builders for the classic shifts calendar grid. The month
// grid renders rows × ~62 cells, so the row template must stay dumb: these
// builders produce render-ready plain objects with no permission checks,
// currency formatting or Vuetify component instances per cell.

export interface CalendarRenderShift { id: number; letter: string; colorClass: string; blocked: boolean }
export interface CalendarRenderCell {
  name: string;
  date: string;
  cellClass: string;
  shifts: CalendarRenderShift[] | null;
  // Patient schedule blocks painted into this day/night sub-cell (patient rows only).
  blocks: BlockSlotCell[];
}
export interface CalendarRenderRow {
  name: string;
  isCaregiverRow: boolean;
  careerColorHex: string;
  cells: CalendarRenderCell[];
}
export type CellColumn = { name: string, date: string, cellClass: string };

export const ESTIMATED_ROW_HEIGHT = 52;
const STACKED_SHIFT_EXTRA_HEIGHT = 28;

export function getShiftStatusColor(shift: ShiftCalendarCell) {
  if (shift.status.color === 'error') return 'black';
  if (shift.status.color === 'warning') return 'warning';
  if (shift.status.color === 'success') return 'success';
  if (shift.status.color === 'grey') return 'grey';

  return 'black';
}

export function buildCellColumns(headers: ShiftCalendarHeader[]): CellColumn[] {
  return headers.flatMap((header) => header.children.map((child) => ({
    name: child.value,
    date: header.date,
    // Weekend shading comes from the backend as cellProps; with a custom row
    // template Vuetify no longer applies it, so the <td> must.
    cellClass: child.cellProps?.class ?? 'pa-0 border-s-sm',
  })));
}

function cellShiftsFor(row: InstanceShifts, column: CellColumn) {
  return row[column.name] as ShiftCalendarCell[] | undefined;
}

export function buildRenderShifts(cellShifts: ShiftCalendarCell[] | undefined) {
  if (!cellShifts) return null;

  return cellShifts.map((shift) => ({
    id: shift.id,
    letter: shift.humanLetter,
    colorClass: `text-${getShiftStatusColor(shift)}`,
    blocked: Boolean(shift.scheduleBlock),
  }));
}

function rowMemberName(row: InstanceShifts) {
  const member = row.caregiver ?? row.patient;

  return member?.fullName ?? '';
}

export function buildRenderRow(
  row: InstanceShifts,
  columns: CellColumn[],
  rowBlocks: BlockSlotCell[] = [],
): CalendarRenderRow {
  return {
    name: rowMemberName(row),
    isCaregiverRow: Boolean(row.caregiver),
    careerColorHex: getCareerColorHex(row.caregiver?.career ?? ''),
    cells: columns.map((column) => ({
      ...column,
      shifts: buildRenderShifts(cellShiftsFor(row, column)),
      blocks: rowBlocks.filter((block) => block.slotName === column.name),
    })),
  };
}

// Sticky "Bloqueos de horario" row shown when a single patient is selected
// (rows are caregivers then, so the patient's blocks need a row of their own).
export function buildBlockRowCells(blocks: BlockSlotCell[], columns: CellColumn[]): CalendarRenderCell[] {
  return columns.map((column) => ({
    ...column,
    shifts: null,
    blocks: blocks.filter((block) => block.slotName === column.name),
  }));
}

// A patient with only a schedule block gets a row without cells
// (GenerateShiftsAsCalendarJob#block_only_patient_rows); the export gate needs shifts.
export function rowsHaveShifts(rows: InstanceShifts[], columns: CellColumn[]) {
  return rows.some((row) => columns.some((column) => Boolean(cellShiftsFor(row, column)?.length)));
}

export function collectShiftsById(rows: InstanceShifts[], columns: CellColumn[]) {
  const map = new Map<number, ShiftCalendarCell>();
  for (const row of rows) {
    for (const column of columns) {
      const cellShifts = cellShiftsFor(row, column);
      if (cellShifts) {
        for (const shift of cellShifts) map.set(shift.id, shift);
      }
    }
  }

  return map;
}

export function estimateRowHeight(row: CalendarRenderRow) {
  let maxStack = 1;
  for (const cell of row.cells) {
    const stacked = (cell.shifts?.length ?? 0) + cell.blocks.length;
    if (stacked > maxStack) maxStack = stacked;
  }

  return ESTIMATED_ROW_HEIGHT + ((maxStack - 1) * STACKED_SHIFT_EXTRA_HEIGHT);
}
